import type { TripState } from '@/types';
import { STATIONS, STATION_INDEX } from '@/data/graph';
import { buildCalendar } from './calendar';
import { haversine } from './geo';
import { fmtDate, fmtHour, fmtTime } from './format';
import { buildShareURL } from './persistence';

/**
 * Sérialise l'itinéraire courant en texte plain (.txt).
 * Format conçu pour être lisible tel quel dans n'importe quel outil.
 *
 * Renvoie le contenu textuel — le téléchargement est géré par l'appelant
 * (pour que ce module reste pur et testable).
 */
export function exportRouteToText(state: TripState): string {
  const calendar = buildCalendar(state);
  const { totals, cal } = calendar;

  const lines: string[] = [];
  lines.push('Mon itinéraire EuroRail');
  lines.push('========================');

  if (calendar.hasDate && calendar.startDate) {
    lines.push(`Départ : ${fmtDate(calendar.startDate)} à ${fmtHour(calendar.startDate)}`);
    if (calendar.endDate) {
      lines.push(`Retour : ${fmtDate(calendar.endDate)} à ${fmtHour(calendar.endDate)}`);
    }
  }
  lines.push(`Mode : ${state.routeMode}`);
  lines.push('');

  state.routeStops.forEach((stop, i) => {
    const s = STATIONS[STATION_INDEX[stop.id]];
    const nights = stop.nights || 0;
    const calItem = cal[i];

    let stopLine = `${i + 1}. ${s.name}`;
    if (calendar.hasDate && calItem.arrive) {
      stopLine += `  [${fmtDate(calItem.arrive)} ${fmtHour(calItem.arrive)}]`;
    }
    lines.push(stopLine);

    if (nights > 0) {
      lines.push(`   Séjour : ${nights} ${nights === 1 ? 'nuit' : 'nuits'}`);
    }

    if (i < state.routeStops.length - 1) {
      const nextStop = state.routeStops[i + 1];
      const nextS = STATIONS[STATION_INDEX[nextStop.id]];
      const km = haversine(s.coords, nextS.coords);
      const path = calItem.segmentPath;
      if (!path) {
        lines.push(`   → ${nextS.name} : non desservi`);
      } else {
        const firstMode = path.segments[0].mode;
        const allSame = path.segments.every((seg) => seg.mode === firstMode);
        const hasNight = path.segments.some(
          (seg) => seg.mode === 'train' && seg.night,
        );
        const modeLabel =
          hasNight && allSame && firstMode === 'train'
            ? 'train de nuit'
            : allSame
              ? firstMode
              : 'mixte';

        let viaInfo = '';
        if (path.segments.length > 1) {
          viaInfo =
            ' (via ' +
            path.segments
              .slice(0, -1)
              .map((seg) => STATIONS[STATION_INDEX[seg.to]].name)
              .join(', ') +
            ')';
        }

        let depLine = `   → ${fmtTime(path.total)} en ${modeLabel} · ${km} km${viaInfo}`;
        if (calendar.hasDate && calItem.depart) {
          depLine += `   [départ ${fmtDate(calItem.depart)} ${fmtHour(calItem.depart)}]`;
        }
        lines.push(depLine);
      }
    }
  });

  lines.push('');
  lines.push('TOTAUX');
  lines.push('------');
  lines.push(
    `Trajet : ${fmtTime(totals.totalMin)} · ${totals.totalKm.toLocaleString('fr-FR')} km`,
  );
  lines.push(`Étapes : ${state.routeStops.length}`);
  lines.push(`Nuits sur place : ${totals.totalNights}`);
  if (totals.nightsSavedByTrain > 0) {
    lines.push(`Nuits gagnées en train : ${totals.nightsSavedByTrain}`);
  }
  if (totals.totalCO2 > 0) {
    const kg = totals.totalCO2 / 1000;
    lines.push(
      `Empreinte CO₂ : ${kg < 1 ? totals.totalCO2 + ' g' : kg.toFixed(1) + ' kg'}`,
    );
  }
  lines.push('');
  lines.push(`Lien : ${buildShareURL(state)}`);

  return lines.join('\n');
}

/**
 * Télécharge le contenu texte sous forme de fichier dans le navigateur.
 * Helper isolé pour pouvoir tester `exportRouteToText` sans toucher au DOM.
 */
export function downloadAsTextFile(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
